import { createFileRoute } from '@tanstack/react-router';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DecoradoFranjaHoraria, FondoOscuroContext } from '../features/auth/components/decoradoFranjaHoraria';
import { FormularioRestablecerContrasena } from '../features/auth/components/formularioRestablecerContrasena';

interface BusquedaRestablecer {
	/** Hash del enlace de recuperación que manda el correo (`/restablecer-contrasena?token_hash=…`). */
	token_hash?: string;
}

/** Igual de laxo que `leerBusquedaLogin` en `routes/login.tsx`: no es un contrato con el backend,
 * es una lectura de `location.search`, así que no hace falta un schema de ArkType para esto. */
const leerBusquedaRestablecer = (busqueda: Record<string, unknown>): BusquedaRestablecer => ({
	token_hash: typeof busqueda.token_hash === 'string' ? busqueda.token_hash : undefined,
});

/**
 * `/restablecer-contrasena` — destino del enlace del correo de recuperación. Sin `beforeLoad`:
 * quien llega aquí no tiene sesión (viene de un correo, no de la app), y un guard de sesión
 * mataría el flujo antes de que `FormularioRestablecerContrasena` pueda leer el `token_hash`.
 */
export const Route = createFileRoute('/restablecer-contrasena')({
	component: PantallaRestablecer,
	// Ruta de acceso (`__root.tsx`): quien llega aquí ya está en pleno trámite de recuperar el
	// acceso, así que un aviso que invita a «iniciar sesión» no pinta nada — sobra igual que en
	// `/login`.
	staticData: { anchoCompleto: true, rutaDeAcceso: true },
	validateSearch: leerBusquedaRestablecer,
});

/**
 * El héroe de `/restablecer-contrasena`: titular y contexto que hasta ahora vivían dentro de
 * `FormularioRestablecerContrasena` (su estado 3, el formulario), mudados aquí para calcar la
 * rejilla de dos columnas de `HeroeLogin` (`routes/login.tsx:123-171`).
 *
 * **Por qué es un componente aparte y no JSX en línea dentro de `PantallaRestablecer`** — el mismo
 * motivo que documenta `HeroeLogin` (`login.tsx:73-82`): `FondoOscuroContext.Provider` vive DENTRO
 * de `DecoradoFranjaHoraria`, envolviendo únicamente su prop `children`. `PantallaRestablecer` es
 * quien MONTA `<DecoradoFranjaHoraria>` — antepasado de ese Provider en el árbol, nunca descendiente
 * — así que un `useContext(FondoOscuroContext)` en su propio cuerpo leería siempre el valor por
 * defecto de `createContext(false)` (claro), jamás el real de la franja horaria activa: el titular
 * sería ilegible en las franjas `atardecer`/`noche` (Bug #2 del repositorio).
 *
 * Marcado calcado de `HeroeLogin`, con dos matices deliberados: el `<h1>` va ANTES del párrafo (a
 * diferencia del orden que tenía el formulario hasta ahora, contexto primero) y la separación es
 * `gap-2` —la de `HeroeLogin`—, no `gap-6`. `text-gray-ink` se aclara solo sobre fondo oscuro por la
 * cascada de `[data-fondo='oscuro']` de `tokens.css` en cualquier descendiente de
 * `DecoradoFranjaHoraria` — no lleva tratamiento condicional, igual que el párrafo base de
 * subtítulo/manifiesto de `HeroeLogin`.
 */
const HeroeRestablecer = (): React.JSX.Element => {
	const { t } = useTranslation('auth');
	const oscuro = useContext(FondoOscuroContext);
	const colorSobreDecorado = oscuro ? 'text-text-on-dark' : 'text-ink';

	return (
		<header className='flex w-full flex-col items-center gap-2 text-center lg:col-start-1 lg:row-start-1'>
			<h1 className={`font-heading text-3xl ${colorSobreDecorado} leading-tight`}>{t('pantalla.tituloRestablecer')}</h1>

			<p className='text-base text-gray-ink'>{t('contexto.restablecer')}</p>
		</header>
	);
};

/**
 * `DecoradoFranjaHoraria` a sangre completa (`anchoCompleto` en `staticData`, el mismo mecanismo
 * que `routes/login.tsx` — ver el JSDoc de `PantallaLogin`, §F-1: sin él el contenedor de lectura de
 * `<main>` seguiría vivo y el decorado nunca ocuparía todo el ancho/alto bajo la cabecera), con la
 * misma rejilla de dos columnas de `login.tsx:254-255`: `HeroeRestablecer` en la columna izquierda,
 * la tarjeta de `FormularioRestablecerContrasena` en la derecha (`448px`, columna fija). Antes de
 * esta tarea la pantalla se pintaba como una `Card` de 768 px flotando sobre `bg-surface-app`
 * (`className` viejo: `-mx-4 -my-8 px-4 py-8 sm:mx-0 sm:my-6 sm:rounded-lg sm:px-10 sm:pt-0
 * sm:pb-14 sm:shadow-float`, que compensaba a mano el contenedor de lectura de `<main>`) — el fallo
 * que corrige esta tarea.
 *
 * A diferencia de `login.tsx`, la columna del formulario NO lleva `lg:row-span-2 lg:self-center`:
 * allí la tarjeta abarca dos filas porque bajo el héroe vive el teaser de roles (`AbanicoRoles`);
 * aquí solo hay una fila y ningún teaser — quien llega desde el correo de recuperación ya tiene
 * cuenta, no hace falta contarle qué es un "grumete".
 *
 * `max-w-md`/`lg:max-w-5xl` son los mismos anchos que `login.tsx`, no una elección libre: la fila
 * del selector de franja horaria (☀⛰☾) de `decoradoFranjaHoraria.tsx` los lleva escritos a fuego,
 * así que reusarlos deja la pastilla alineada sobre la tarjeta sin tocar ese fichero. Por el mismo
 * motivo que ya documenta `login.tsx` (esta rejilla se ha pagado tres veces en el repositorio, de
 * 448 a ~292 px y de 768 a 749 px): el envoltorio de la rejilla es el ÚNICO `mx-auto` de la zona y
 * sobrevive porque declara `w-full`; sus dos hijos llevan `w-full` propio y ninguno un `mx-auto`
 * propio — un hijo con márgenes automáticos en el eje transversal deja de estirarse y se encoge a su
 * contenido (spec de Flexbox §8.3).
 */
function PantallaRestablecer(): React.JSX.Element {
	const { token_hash: tokenHash } = Route.useSearch();

	return (
		<DecoradoFranjaHoraria className='px-4 pt-4 pb-24 sm:px-6 sm:pt-6 sm:pb-28 lg:pb-16'>
			<div className='mx-auto flex w-full max-w-md flex-col gap-10 lg:grid lg:max-w-5xl lg:grid-cols-[minmax(0,1fr)_448px] lg:items-start lg:gap-x-12 lg:my-auto'>
				<HeroeRestablecer />

				<div className='flex w-full flex-col gap-8 lg:col-start-2 lg:row-start-1'>
					<FormularioRestablecerContrasena tokenHash={tokenHash} />
				</div>
			</div>
		</DecoradoFranjaHoraria>
	);
}
